import angular from 'angular'
import article from 'indefinite-article'

import KEY from '../../constants/vd-key-codes'
import popoverTemplate from './vd-dropdown-popover.html'

/**
 * Controller for the vd-dropdown component.
 *
 * @class DropdownController
 */
export default class DropdownController {
  constructor($element, $attrs, $scope, $log, $document, vdSuggestionPopover) {
    'ngInject'
    this._$element = $element
    this._$attrs = $attrs
    this._$scope = $scope
    this._$log = $log
    this._$document = $document
    this._vdSuggestionPopover = vdSuggestionPopover

    this.getSelectedValue = () => this._getSelectedValue()
  }

  $onInit() {
    this.source.init()
  }

  $postLink() {
    this._$attrs.$addClass('vd-select-container vd-dropdown-input-container')

    if (this.inline) {
      this._$attrs.$addClass('vd-select-container--inline')
    }

    this._inputElement = angular.element(
      this._$element[0].querySelector('.vd-dropdown-input')
    )

    this._inputElement.on('focus', () => this.ngModelCtrl.$setTouched())
    this._inputElement.on('blur', () => {
      if (!this._vdSuggestionPopover.isOpen) {
        this._setModelDirty()
      }
    })
  }

  /**
   * Get the placeholder text to display if there's no selected value.
   *
   * @method getPlaceholderText
   *
   * @return {String} The placeholder text to display if there's no value
   */
  getPlaceholderText() {
    const type = this.source && this.source.getTypeDisplay()
    if (!type) {
      return
    }

    const choose = `${
      !this.inline || this.case === 'sentence' ? 'C' : 'c'
    }hoose`
    return this.source && `${choose} ${article(type)} ${type}`
  }

  /**
   * Toggle display of the suggestion list popover.
   *
   * @method toggleSuggestions
   */
  toggleSuggestions() {
    if (this._vdSuggestionPopover.isOpen) {
      this._vdSuggestionPopover.close()
    } else {
      this._openSuggestionList()
    }
  }

  /**
   * Handle key press, opening the suggestion list on Enter or Space key press.
   *
   * @method onKeyPress
   * @param  {Event} $event
   */
  onKeyPress($event) {
    const key = $event.which
    if (
      (key === KEY.ENTER || key === KEY.SPACE) &&
      !this._vdSuggestionPopover.isOpen
    ) {
      this._openSuggestionList()
    }
  }

  _getSelectedValue() {
    if (this.ngModelCtrl.$modelValue) {
      return this.source.getDisplayText(this.ngModelCtrl.$modelValue)
    }

    return this.source.noSelectionLabel
  }

  _openSuggestionList() {
    this._suggestionListHasBeenOpened = true
    this.source
      .search({})
      .then(({ suggestions, groups }) => {
        const popoverOptions = {
          trigger: this._$element,
          template: popoverTemplate,
          source: this.source,
          suggestions,
          groups,
          selected: this.ngModelCtrl.$modelValue,
          allowClear: this._allowClear(),
          // Default to lazy load suggestions (uses `vs-repeat` as opposed to `ng-repeat`)
          lazyLoad: this.lazyLoad === undefined ? true : this.lazyLoad,
          prefill: this.prefill,
        }

        if (this.inline || this.fluidWidth) {
          popoverOptions.inheritWidthFromTrigger = 'min-width'
        }

        this._vdSuggestionPopover.open(popoverOptions).then(suggestion => {
          if (suggestion && typeof suggestion === 'string') {
            this._create(suggestion)
          } else {
            this._select(suggestion)
          }
        })
      })
      .catch(error => {
        this._$log.error('[VD-DROPDOWN] Error Searching Source', { error })
        this._vdSuggestionPopover.openError({ trigger: this._$element })
      })
  }

  _allowClear() {
    return !this._$attrs.required && !this.source.noSelectionLabel
  }

  _select(suggestion) {
    this._focusInputOrSetModelDirty()

    if (suggestion) {
      this._$scope.$applyAsync(() => {
        this.ngModelCtrl.$setViewValue(suggestion)
      })
      return
    }

    if (
      suggestion === null &&
      (this.source.noSelectionLabel || this._allowClear())
    ) {
      this._$scope.$applyAsync(() => this.ngModelCtrl.$setViewValue())
    }
  }

  _create(query) {
    this.source.create({ query }).then(result => this._select(result))
  }

  _focusInputOrSetModelDirty() {
    const activeElement = this._$document[0].activeElement
    if (!activeElement || activeElement.nodeName.toLowerCase() === 'body') {
      this._inputElement[0].focus()
    } else if (!this._inputElement[0].matches(':focus')) {
      this._setModelDirty()
    }
  }

  _setModelDirty() {
    if (this._suggestionListHasBeenOpened) {
      this._$scope.$applyAsync(() => this.ngModelCtrl.$setDirty())
    }
  }
}
